// Home screen. The world stage (behind every tab) shows the island; this screen is the HUD
// floating over it and wires the island's taps to the app. One fact (WI-04): the trip pill top
// left; at the bottom, the stop bar that walks the journey. The action card that sat under the
// pill took too much of the island (owner, 23 Oct): its cues are badges on the dock now.
// Low battery, no WebGL or a lost context show the flat trip map instead.
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import type { Person } from '../../shared/events'
import type { Pack } from '../../shared/pack/schema'
import { closeOverlay, openOverlay, useRoute } from '../app/router'
import { ClimatePill, MeButton, OfflineBanner, SyncPill, TierPill, TripPill } from '../app/StatusBits'
import type { TripNow } from '../app/useTripNow'
import { record } from '../data/events'
import { useSettings } from '../data/settings'
import type { Figure } from '../../shared/family'
import { figureFor } from '../../shared/figures'
import { useEvents } from '../data/progress'
import { useSession } from '../data/session'
import { myPlaces } from '../domain/places'
import { islandNow, flagsFor, mapView, pinsFor, shownIslands } from '../map/bindings'
import { journeyStops, nowStop, pillText, stopFor, type JourneyStop } from '../map/journey'
import { FlatMap } from '../map/FlatMap'
import { SfxToast, useSfx } from '../map/SfxToast'
import { WorldPanel } from '../map/WorldPanel'
import { useMotionTier } from '../motion/tier'
import { ALL_ISLANDS, goStop, islandOrder, setSpottedBy, useStage } from '../stage/bridge'
import type { WorldState } from '../stage/types'
import { t } from '../strings/en'
import { placeId, PLACES } from '../map/places/registry'
import { usePacks } from '../data/packs'
import { wrappedBy } from '../domain/packs'
import { WrappedIsland } from './WrappedIsland'
import { devFlag } from '../dev/flags'
import { playsOf } from '../domain/players'
import { useRewardWorld } from '../arcade/useArcade'

/** e2e: sessionStorage `tq-world-open` = 1 opens every act (no WI-13 locks), like `tq-force-full`. */
function worldOpenPinned(): boolean {
  try {
    return sessionStorage.getItem('tq-world-open') === '1'
  } catch {
    return false
  }
}

/** The showing island's own hint (strings: map.placeHints), for "tap the streetcar…". */
const islandHint = (id: string): string | undefined => t.map.placeHints[placeId(id)]
/** The World panel's overlay key. */
const WORLD = 'world'
/**
 * The "drag to spin · pinch to zoom" hint shows on a person's first few Map visits on this phone,
 * then never again (owner, 23 Oct, round 2: room for the island).
 */
export const HINT_VISITS = 3
/** Auto-hide (dev flag): this long without a touch, then a touch this near the bottom restores it. */
const AUTOHIDE_MS = 4000
const AUTOHIDE_ZONE_PX = 180
const visitsKey = (personId: string) => `tq-map-visits:${personId}`

/** Counts this visit and says whether the hint still shows. Per-phone convenience only: storage
 * can be unavailable (private mode), so it never throws. */
function countVisit(personId: string): boolean {
  try {
    const n = Number(localStorage.getItem(visitsKey(personId)) ?? 0) + 1
    localStorage.setItem(visitsKey(personId), String(n))
    return n <= HINT_VISITS
  } catch {
    return true
  }
}

export function MapScreen({ pack: trip, person, now, world, flat }: { pack: Pack; person: Person; now: TripNow; world: WorldState; flat: boolean }) {
  // Stages outside Japan stand on Castle Town on the Map until the generic island is picked (B3).
  const pack = useMemo(() => mapView(trip), [trip])
  const tier = useMotionTier()
  const resting = useStage((s) => s.status) !== 'failed' && flat
  const interacted = useStage((s) => s.interacted)
  const act = useStage((s) => s.act)
  const island = useStage((s) => s.island)
  // A child can't see every stage (shared/controls.ts): only islands of visible stages are offered,
  // and the Map never stays on one that's hidden (that would give the surprise away). World →
  // Explore all (per session, off by default) offers every island and place instead; grown-ups only,
  // since it lists the islands of hidden stages (N3).
  const child = person.kind === 'child'
  const explore = useStage((s) => s.explore) && !child
  const allIds = ALL_ISLANDS.map((i) => i.id)
  const allowed = explore ? allIds : shownIslands(pack, allIds)
  // Explore all's switcher: the trip's places first (stage order), then the rest.
  const tripPlaces = useStage((s) => s.tripPlaces)
  const islands = islandOrder(tripPlaces).filter((i) => allowed.includes(i.id))
  // C6: islands of a pack the family doesn't own are wrapped (domain/packs), the free one isn't.
  const catalogue = usePacks((s) => s.catalogue)
  const owned = usePacks((s) => s.owned)
  const wrapOf = (id: string | null) => wrappedBy({ catalogue, owned, islandId: id, country: PLACES.find((x) => x.id === id)?.country })
  const wrapped = new Set(islands.filter((i) => wrapOf(i.id)).map((i) => i.id))
  const wrapHere = wrapOf(island)
  const shownKey = allowed.join(',')
  useEffect(() => {
    useStage.getState().set({ islandsShown: shownKey ? shownKey.split(',') : [] })
  }, [shownKey])
  // Off a hidden (or another trip's) island: to the island of the stage we're in now (or next),
  // else the first visible stage's (B8).
  const first = islandNow(pack, now.clock, allowed) ?? allowed[0]
  useEffect(() => {
    if (!island || !first || shownKey.split(',').includes(island)) return
    const s = useStage.getState()
    s.set({ goIsland: { id: first, n: (s.goIsland?.n ?? 0) + 1 } })
  }, [island, shownKey, first])

  // The journey's stops (repeats included) and the one we're at; the bar shows `stop`.
  const stops = useMemo(() => journeyStops(pack.itinerary.stages, trip.itinerary.stages), [pack, trip])
  const stopsKey = stops.map((s) => s.spot ?? '').join(',')
  const nowIdx = nowStop(now.clock, stops.length)
  const stop = useStage((s) => s.stop)
  // The stop follows the island showing: kept while it's on that island (or flying there, or a
  // wrapped surprise with no island of its own), else the stop we're at when it's on this island,
  // else its first visit, else none (an Explore-all island off the trip).
  const prevIsland = useRef(island)
  useEffect(() => {
    const s = useStage.getState()
    const moved = prevIsland.current !== island
    prevIsland.current = island
    const cur = s.stop !== null ? stops[s.stop] : undefined
    if (cur && (cur.spot === island || (!moved && (cur.spot === null || cur.spot === s.goIsland?.id)))) return
    const next = stopFor(stops, island, nowIdx)
    if (next !== s.stop) s.set({ stop: next })
    // eslint-disable-next-line react-hooks/exhaustive-deps -- `stops` is keyed by stopsKey
  }, [island, stopsKey, nowIdx])
  const away = stop === null ? island !== stops[nowIdx]?.spot : stop !== nowIdx
  const backToNow = () => {
    goStop(nowIdx, stops[nowIdx]?.spot ?? null)
    useStage.getState().set({ home: useStage.getState().home + 1 })
  }

  // This person's first few visits show the hint (it also fades once the island is dragged).
  const [hint] = useState(() => countVisit(person.id))
  // Auto-hide, to try (dev flag tq-dev-autohide, off by default): after AUTOHIDE_MS without a touch
  // the stop bar shrinks to its dots; a touch near the bottom or on the top pill brings it back.
  const [autoHide] = useState(() => devFlag('tq-dev-autohide'))
  const [mini, setMini] = useState(false)
  useEffect(() => {
    if (!autoHide) return
    let timer = setTimeout(() => setMini(true), AUTOHIDE_MS)
    const down = (e: PointerEvent) => {
      clearTimeout(timer)
      const el = e.target instanceof Element ? e.target : null
      if (e.clientY > innerHeight - AUTOHIDE_ZONE_PX || el?.closest('.trip-pill, .stop-bar')) setMini(false)
      timer = setTimeout(() => setMini(true), AUTOHIDE_MS)
    }
    addEventListener('pointerdown', down, true)
    return () => {
      clearTimeout(timer)
      removeEventListener('pointerdown', down, true)
    }
  }, [autoHide])
  const hudTop = useRef<HTMLDivElement>(null)
  const hudBottom = useRef<HTMLDivElement>(null)
  const route = useRoute()
  // The World panel is an overlay in the URL (?o=world), so Back closes it.
  const worldOpen = route.overlay === WORLD
  const vibration = useSettings((s) => s.vibration)
  const showSfx = useSfx((s) => s.show)
  const pins = useMemo(() => pinsFor(pack, now.clock), [pack, now.clock])
  // Want-to-go places as flags on their island (F4); a tap opens the place card.
  const events = useEvents()
  const wantedIds = [...myPlaces(events, person.id).wanted.keys()].join(',')
  const flags = useMemo(() => flagsFor(pack, pack.places.filter((p) => wantedIds.split(',').includes(p.id))), [pack, wantedIds])

  // What the island shows, and what its taps do.
  useEffect(() => useStage.getState().set({ pins }), [pins])
  useEffect(() => useStage.getState().set({ flags }), [flags])
  // A player's own figure stands at "you are here" (grown-ups who don't play see the island without one).
  const people = useSession((s) => s.people)
  const heroKey = playsOf(person) ? JSON.stringify(figureFor(person, people ?? [])) : ''
  useEffect(() => {
    useStage.getState().set({ hero: heroKey ? (JSON.parse(heroKey) as Figure) : null })
  }, [heroKey])
  // WI-13, a world per child: a player's locked acts stay quiet until their key or arrival.
  // (e2e: sessionStorage `tq-world-open` opens everything, for the act and tap specs.)
  const { world: rewardWorld } = useRewardWorld(pack, person.id)
  const lockedKey = playsOf(person) && !worldOpenPinned() ? JSON.stringify(rewardWorld.locked) : '{}'
  useEffect(() => {
    useStage.getState().set({ lockedActs: JSON.parse(lockedKey) as Record<string, string[]> })
  }, [lockedKey])
  // Spotted is each person's own too.
  useEffect(() => setSpottedBy(person.id), [person.id])
  const overlayRef = useRef(route.overlay)
  useEffect(() => {
    overlayRef.current = route.overlay
  })
  useEffect(() => {
    const bump = () => useStage.getState().set({ home: useStage.getState().home + 1 })
    useStage.getState().set({
      map: {
        onPin: (id) => openOverlay(`stage:${id}`),
        onFlag: (id) => openOverlay(`place:${id}`),
        onEmpty: () => {
          // A card closes on a tap on the island; the World panel stays while the island is played with.
          if (overlayRef.current && overlayRef.current !== WORLD) closeOverlay()
          bump()
        },
        onSfx: (card, at) => {
          // A rare or legendary act's word stays up until the act ends (WI-06).
          const rarity = useStage.getState().act?.rarity
          showSfx(card, at, { untilActEnds: !!at && (rarity === 'rare' || rarity === 'legendary') })
          if (vibration) navigator.vibrate?.(25)
          // Deck words count as seen; a place's inline words aren't deck cards (docs/PLACES.md).
          if (typeof card === 'string') void record('sfx.triggered', { cardId: card }, person.id)
        },
        onInteract: () => {},
        onAct: () => {},
        onSpotted: () => {},
        onIsland: () => {},
      },
    })
  }, [showSfx, vibration, person.id])

  // Labels hide behind the HUD, and the island is framed in the strip between (WI-03). Glove mode's
  // bigger tab bar moves the stop bar up without resizing it: measure again then too.
  const glove = useSettings((s) => s.glove)
  useLayoutEffect(() => {
    const measure = () => {
      const top = hudTop.current?.getBoundingClientRect().bottom ?? 0
      const bottom = hudBottom.current?.getBoundingClientRect().top ?? Infinity
      const cur = useStage.getState().hud
      if (cur.top !== top || cur.bottom !== bottom) useStage.getState().set({ hud: { top, bottom } })
    }
    measure()
    const ro = new ResizeObserver(measure)
    if (hudTop.current) ro.observe(hudTop.current)
    if (hudBottom.current) ro.observe(hudBottom.current)
    window.addEventListener('resize', measure)
    // The glove attribute is set in App's effect, after this one: measure on the next frame too.
    const raf = requestAnimationFrame(measure)
    return () => {
      cancelAnimationFrame(raf)
      ro.disconnect()
      window.removeEventListener('resize', measure)
    }
  }, [flat, glove])

  // Any sheet (or the World panel) over the Map pauses the island's idle turn.
  const sheet = !!route.overlay
  useEffect(() => {
    useStage.getState().set({ sheet })
    return () => useStage.getState().set({ sheet: false })
  }, [sheet])

  // Closing a card eases the camera back to the home view (closing the World panel doesn't).
  const prevOverlay = useRef(route.overlay)
  useEffect(() => {
    if (prevOverlay.current && prevOverlay.current !== WORLD && !route.overlay) useStage.getState().set({ home: useStage.getState().home + 1 })
    prevOverlay.current = route.overlay
  }, [route.overlay])

  return (
    <main className={`screen map-screen ${flat ? 'flat' : 'live'}`}>
      {/* For screen readers: the screen's heading names the place showing (WI-28). */}
      <h1 className="map-h1">{t.map.heading(stop !== null && stops[stop] ? (stops[stop].place ?? stops[stop].name) : (ALL_ISLANDS.find((i) => i.id === island)?.title ?? ''))}</h1>
      <div className="map-hud-top" ref={hudTop}>
        <OfflineBanner />
        <div className="top-row">
          {/* In parent mode this is the parent-mode pill (WI-17); a sheet over the Map covers it. */}
          <TripPill text={pillText(now.clock, stops)} onBack={away && !flat ? backToNow : undefined} covered={!!route.overlay && !worldOpen} />
          <span className="spacer" />
          {/* WI-40 #4: an icon, so the trip pill keeps its words; it gives up room last. */}
          <TierPill tier={tier} icon />
          <SyncPill />
          {/* The World panel is for everyone (time, weather, spotted); a child's has no Surprise
              buttons and no Explore all (N3: they give hidden stages away). */}
          {!flat ? (
            <button className="pill press" onClick={() => (worldOpen ? closeOverlay() : openOverlay(WORLD))} aria-expanded={worldOpen}>
              {t.map.worldButton}
              <span aria-hidden>▾</span>
            </button>
          ) : (
            <ClimatePill pack={pack} clock={now.clock} nowMs={now.nowMs} />
          )}
          <MeButton person={person} />
        </div>
        {!flat && explore ? (
          <IsleSwitch
            islands={islands}
            wrapped={wrapped}
            current={island}
            onPick={(id) => {
              const s = useStage.getState()
              if (s.island !== id) s.set({ goIsland: { id, n: (s.goIsland?.n ?? 0) + 1 } })
            }}
          />
        ) : null}
      </div>

      {flat ? (
        <>
          <section className="panel flat-panel" aria-label={t.map.flatTitle}>
            <div className="mono">{t.map.flatTitle}</div>
            <FlatMap pack={pack} clock={now.clock} />
          </section>
          <p className="caption">{resting ? t.map.resting : t.map.fallback}</p>
        </>
      ) : (
        <>
          {/* Always mounted, so screen readers hear each act as it starts (L6-13, WI-06). Visually
              hidden: the act's name shows as one line in the sound-word band (SfxToast). */}
          <div className="act-ticker" role="status" aria-live="polite">
            {act ? `${t.map.world.onIsland}: ${act.hint ?? `${act.label} · ${act.variant}`}` : null}
          </div>
          {wrapHere && island ? <WrappedIsland pack={wrapHere} trip={trip} person={person} islandId={island} /> : null}
          {!act && hint ? (
            <p className={`map-hint${world.tod === 2 ? ' on-dark' : ''}`} style={{ opacity: interacted ? 0 : 1 }}>
              {t.map.hint(islandHint(island))}
            </p>
          ) : null}
          <StopBar ref={hudBottom} mini={mini} stops={stops} viewed={stop} now={nowIdx} island={island} onGo={(i) => goStop(i, stops[i]?.spot ?? null)} />
        </>
      )}

      {worldOpen && !flat ? <WorldPanel child={child} onClose={closeOverlay} /> : null}
      {!flat ? <SfxToast pack={pack} tier={tier} /> : null}
    </main>
  )
}

/** Dots shrink past this many stops, so the row never wraps. */
const ROOMY_STOPS = 8
/** A horizontal drag this long on the bar steps one stop. */
const SWIPE_PX = 36

/**
 * The stop bar above the dock (WI-04; slim since round 2: one floating line, no box, tiny dots;
 * `mini`, behind tq-dev-autohide: just the dots): ‹ and › step one stop (hidden, not removed, at the ends),
 * a dot jumps to its stop, a swipe on the bar steps too. The caption names the stop showing; the
 * yellow dot is the one showing, the blue ring the one we're at.
 */
function StopBar({ stops, viewed, now, island, onGo, ref, mini = false }: { stops: JourneyStop[]; viewed: number | null; now: number; island: string; onGo(i: number): void; ref: React.Ref<HTMLDivElement>; mini?: boolean }) {
  const swipe = useRef<{ x: number; y: number; done: boolean } | null>(null)
  const n = stops.length
  const at = viewed === null ? null : stops[viewed]
  // Off the trip (Explore all), the arrows lead back onto it at the stop we're at.
  const step = (d: -1 | 1) => onGo(viewed === null ? now : Math.max(0, Math.min(n - 1, viewed + d)))
  // Tiny dots under the caption (round 2: the slim bar), each with a 44 px tall tap area.
  const dot = n <= ROOMY_STOPS ? 8 : Math.max(4, Math.round((8 * ROOMY_STOPS) / n))
  const gap = n <= ROOMY_STOPS ? 14 : Math.max(6, Math.round((14 * ROOMY_STOPS) / n))
  return (
    <div
      className={`stop-bar${mini ? ' mini' : ''}`}
      ref={ref}
      role="group"
      aria-label={t.map.stops.label}
      onPointerDown={(e) => {
        swipe.current = { x: e.clientX, y: e.clientY, done: false }
      }}
      onPointerMove={(e) => {
        const s = swipe.current
        if (!s || s.done) return
        const dx = e.clientX - s.x
        if (Math.abs(dx) > SWIPE_PX && Math.abs(dx) > Math.abs(e.clientY - s.y) * 1.5) {
          s.done = true
          step(dx < 0 ? 1 : -1)
        }
      }}
      onClickCapture={(e) => {
        // The click that ends a swipe isn't a tap on a dot or an arrow.
        if (swipe.current?.done) {
          e.stopPropagation()
          e.preventDefault()
        }
        swipe.current = null
      }}
    >
      <button className="stop-step press" aria-label={t.map.stops.prev} style={{ visibility: viewed === 0 ? 'hidden' : undefined }} onClick={() => step(-1)}>
        ‹
      </button>
      <div className="stop-mid">
        <span className="stop-caption">
          <b>{at ? at.name : (ALL_ISLANDS.find((i) => i.id === island)?.title ?? '')}</b>
          {at?.dates ? <small> · {at.dates}</small> : null}
        </span>
        <span className="stop-dots" style={{ '--dot': `${dot}px`, '--gap': `${gap}px` } as React.CSSProperties}>
          {stops.map((s, i) => (
            <button
              key={s.stageId}
              className={`stop-dot${i === viewed ? ' sel' : ''}${i === now ? ' now' : ''}${s.wrapped ? ' wrapped' : ''}`}
              aria-label={t.map.stops.dot(i + 1, s.name, s.dates)}
              aria-current={i === viewed ? 'step' : undefined}
              onClick={() => onGo(i)}
            >
              {s.wrapped ? <span aria-hidden>🎁</span> : null}
            </button>
          ))}
        </span>
      </div>
      <button className="stop-step press" aria-label={t.map.stops.next} style={{ visibility: viewed === n - 1 ? 'hidden' : undefined }} onClick={() => step(1)}>
        ›
      </button>
    </div>
  )
}

/**
 * Explore all's island switcher (grown-ups, World panel; the trip itself is walked with the stop
 * bar). It holds every place, more than fits, so it scrolls sideways: by swipe on a phone, by the
 * mouse wheel on a PC (vertical wheel moves it sideways), and by the ‹ › buttons that show while
 * there's more that way. The current island scrolls into view.
 */
function IsleSwitch({ islands, wrapped, current, onPick }: { islands: { id: string; title: string }[]; wrapped: ReadonlySet<string>; current: string | null; onPick(id: string): void }) {
  const strip = useRef<HTMLDivElement>(null)
  const [more, setMore] = useState({ left: false, right: false })

  const measure = () => {
    const el = strip.current
    if (!el) return
    const left = el.scrollLeft > 2
    const right = el.scrollLeft + el.clientWidth < el.scrollWidth - 2
    setMore((m) => (m.left === left && m.right === right ? m : { left, right }))
  }

  useEffect(() => {
    const el = strip.current
    if (!el) return
    measure()
    const ro = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(measure)
    ro?.observe(el)
    // A vertical wheel (most mice) scrolls the strip sideways; it needs a non-passive listener.
    const wheel = (e: WheelEvent) => {
      if (el.scrollWidth <= el.clientWidth || Math.abs(e.deltaX) > Math.abs(e.deltaY)) return
      e.preventDefault()
      el.scrollBy({ left: e.deltaY })
    }
    el.addEventListener('wheel', wheel, { passive: false })
    return () => {
      ro?.disconnect()
      el.removeEventListener('wheel', wheel)
    }
  }, [islands.length])

  // Keep the current island in view (e.g. after picking one near the edge, or on open).
  useEffect(() => {
    const btn = strip.current?.querySelector<HTMLElement>('button[aria-pressed="true"]')
    btn?.scrollIntoView({ block: 'nearest', inline: 'nearest', behavior: 'smooth' })
  }, [current, islands.length])

  const page = (dir: -1 | 1) => {
    const el = strip.current
    if (el) el.scrollBy({ left: dir * Math.max(120, el.clientWidth * 0.7), behavior: 'smooth' })
  }

  return (
    <div className={`isle-switch-wrap${more.left ? ' more-left' : ''}${more.right ? ' more-right' : ''}`}>
      {more.left ? (
        <button className="isle-more left press" aria-label={t.map.islandsBack} onClick={() => page(-1)}>
          ‹
        </button>
      ) : null}
      <div className="isle-switch" role="group" aria-label={t.map.islands} ref={strip} onScroll={measure}>
        {islands.map((i) => (
          // The button is the 44 px tap target; the pill inside it is what shows.
          <button key={i.id} className="press" aria-pressed={current === i.id} onClick={() => onPick(i.id)}>
            <span>
              {wrapped.has(i.id) ? <span aria-hidden>🎀 </span> : null}
              {i.title}
            </span>
          </button>
        ))}
      </div>
      {more.right ? (
        <button className="isle-more right press" aria-label={t.map.islandsMore} onClick={() => page(1)}>
          ›
        </button>
      ) : null}
    </div>
  )
}
